import React, { useState, useEffect, useContext } from "react";
import { certificatesAPI, emailAPI, eventsAPI } from "../../lib/apiService";
import { APIRequestContext } from "../../context";

function AdminDashboard() {
  const [certificates, setCertificates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [mappingFile, setMappingFile] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const { request_handler } = useContext(APIRequestContext);

  useEffect(() => {
    loadCertificates();
    loadEmailStatus();
    loadEvents();
  }, []);

  const loadEvents = () => {
    request_handler(async () => {
      const response = await eventsAPI.getAll();
      const eventsList = response.data || response;
      setEvents(eventsList);
      
      // Auto-select first event if available
      if (eventsList.length > 0 && !selectedEventId) {
        setSelectedEventId(eventsList[0].event_id || eventsList[0].id);
      }
      
      return { data: { message: "Events loaded successfully" } };
    });
  };

  const loadCertificates = () => {
    request_handler(async () => {
      const response = await certificatesAPI.getAll();
      setCertificates(response.data || response);
      return { data: { message: "Certificates loaded successfully" } };
    });
  };

  const loadEmailStatus = () => {
    request_handler(async () => {
      const response = await emailAPI.getEmailStatus();
      setEmailStatus(response.data || response);
      return response;
    });
  };

  const handleTemplateUpload = (e) => {
    e.preventDefault();
    const fileInput = e.target.template;
    
    if (!fileInput.files[0]) {
      return;
    }

    request_handler(async () => {
      const response = await certificatesAPI.uploadTemplate(fileInput.files[0]);
      return response;
    });
  };

  const handleBulkGeneration = (e) => {
    e.preventDefault();
    
    if (!csvFile || !selectedTemplate) {
      alert('Please select both CSV file and template');
      return;
    }

    if (!selectedEventId) {
      alert('Please select an event');
      return;
    }

    request_handler(async () => {
      // Convert to integer
      const eventIdInt = parseInt(selectedEventId, 10);
      
      const response = await certificatesAPI.generateBulk(
        csvFile,
        selectedTemplate,
        mappingFile,
        eventIdInt,
        'manual'
      );
      
      loadCertificates(); // Reload certificates after generation
      return response;
    });
  };

  const handleSingleGeneration = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    if (!selectedEventId) {
      alert('Please select an event');
      return;
    }

    request_handler(async () => {
      // Convert to integer
      const eventIdInt = parseInt(selectedEventId, 10);
      
      const certificateData = {
        name: formData.get('name'),
        email: formData.get('email'),
        eventId: eventIdInt,
      };
      
      const response = await certificatesAPI.generateSingle(certificateData);
      loadCertificates(); // Reload certificates after generation
      return response;
    });
  };

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>
      
      {/* Event Selection Section */}
      <div className="section">
        <h3>Select Event</h3>
        <div className="event-selection">
          <select 
            value={selectedEventId} 
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="event-dropdown"
          >
            <option value="">-- Select Event --</option>
            {events.map(event => (
              <option 
                key={event.event_id || event.id} 
                value={event.event_id || event.id}
              >
                {event.name} {event.date && `(${new Date(event.date).toLocaleDateString()})`}
              </option>
            ))}
          </select>
          {selectedEventId && (
            <div className="selected-event-info">
              <p>✅ Selected Event ID: <strong>{selectedEventId}</strong></p>
            </div>
          )}
        </div>
      </div>

      {/* Template Upload Section */}
      <div className="section">
        <h3>Upload Template</h3>
        <form onSubmit={handleTemplateUpload}>
          <input type="file" name="template" accept="image/*" required />
          <button type="submit">Upload Template</button>
        </form>
      </div>

      {/* Bulk Certificate Generation */}
      <div className="section">
        <h3>Bulk Certificate Generation</h3>
        <form onSubmit={handleBulkGeneration}>
          <div className="form-group">
            <label>CSV File:</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files[0])}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Template:</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setSelectedTemplate(e.target.files[0])}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Mapping File (optional):</label>
            <input
              type="file"
              accept=".json"
              onChange={(e) => setMappingFile(e.target.files[0])}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={!selectedEventId}
            className={!selectedEventId ? 'disabled' : ''}
          >
            Generate Bulk Certificates
          </button>
        </form>
      </div>

      {/* Single Certificate Generation */}
      <div className="section">
        <h3>Generate Single Certificate</h3>
        <form onSubmit={handleSingleGeneration}>
          <div className="form-group">
            <label>Name:</label>
            <input type="text" name="name" required />
          </div>
          
          <div className="form-group">
            <label>Email:</label>
            <input type="email" name="email" required />
          </div>
          
          <button 
            type="submit" 
            disabled={!selectedEventId}
            className={!selectedEventId ? 'disabled' : ''}
          >
            Generate Single Certificate
          </button>
        </form>
      </div>

      {/* Certificates List */}
      <div className="section">
        <h3>Recent Certificates ({certificates.length})</h3>
        <div className="certificates-list">
          {certificates.slice(0, 10).map(cert => (
            <div key={cert.id} className="certificate-item">
              <p><strong>{cert.name}</strong> - {cert.email}</p>
              <p>Event ID: {cert.eventId} | Generated: {new Date(cert.createdAt).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Email Status */}
      {emailStatus && (
        <div className="section">
          <h3>Email Status</h3>
          <p>Status: {emailStatus.status}</p>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
